
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { toast } from "sonner";
import { EmailTemplate, EmailTemplateActions } from "@/types/emailTemplates";
import * as emailTemplateService from "@/services/emailTemplate";
import { useQuery } from "@tanstack/react-query";

export function useEmailTemplates(page = 1, pageSize = 10): EmailTemplateActions & {
  loading: boolean;
  templates: EmailTemplate[];
  loadingAction: boolean;
  error: Error | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
  };
} {
  const [loadingAction, setLoadingAction] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { toast: uiToast } = useToast();

  // Use React Query with optimized settings
  const { data, isLoading, refetch, isError, error: queryError } = useQuery({
    queryKey: ['emailTemplates', page, pageSize],
    queryFn: () => emailTemplateService.fetchTemplates(page, pageSize),
    staleTime: 1 * 60 * 1000, // 1 minute cache (reduced from 5)
    gcTime: 2 * 60 * 1000, // 2 minutes garbage collection time (reduced from 10)
    retry: 2, // Reduce retry attempts to prevent excessive resource usage
    retryDelay: (attempt) => Math.min(attempt > 1 ? 2 ** attempt * 1000 : 1000, 10 * 1000), // Cap at 10 seconds
    refetchOnWindowFocus: false, // Prevent unnecessary refetches
    placeholderData: (prevData) => prevData, // Use previous data as placeholder
  });

  // Provide empty arrays for templates if data is undefined to prevent errors
  const templates = data?.templates || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  // If we have a query error, update our local error state
  if (isError && queryError instanceof Error && !error) {
    setError(queryError);
    // Log error and show toast when an error occurs
    console.error("Email templates fetch error:", queryError);
    toast.error("Failed to load templates: " + queryError.message);
  }

  // Efficiently fetch templates with optimized error handling
  const fetchTemplates = async (): Promise<EmailTemplate[]> => {
    try {
      const result = await refetch();
      if (result.error) throw result.error;
      return result.data?.templates || [];
    } catch (err: any) {
      setError(err);
      toast.error("Failed to refresh templates");
      return [];
    }
  };

  const fetchTemplate = async (id: string): Promise<EmailTemplate | null> => {
    try {
      setError(null);
      console.log(`Fetching individual template with ID: ${id}`);
      const template = await emailTemplateService.fetchTemplate(id);
      
      if (!template) {
        throw new Error(`Template with ID ${id} not found`);
      }
      
      return template;
    } catch (err: any) {
      console.error(`Error fetching template ${id}:`, err);
      setError(err);
      toast.error("Failed to load template");
      return null;
    }
  };

  const createTemplate = async (
    template: Omit<EmailTemplate, 'id' | 'created_at' | 'updated_at' | 'version'>
  ): Promise<EmailTemplate | null> => {
    try {
      setLoadingAction(true);
      setError(null);
      
      const result = await emailTemplateService.createTemplate(template);
      
      toast.success("Template created successfully");

      // Update local state with refetch
      await refetch();
      return result;
    } catch (err: any) {
      setError(err);
      toast.error("Failed to create template: " + err.message);
      return null;
    } finally {
      setLoadingAction(false);
    }
  };

  const updateTemplate = async (
    id: string,
    template: Partial<EmailTemplate>
  ): Promise<EmailTemplate | null> => {
    try {
      setLoadingAction(true);
      setError(null);
      
      console.log(`Updating template with ID: ${id}`, template);
      const data = await emailTemplateService.updateTemplate(id, template);

      toast.success(`Template "${data.name}" updated`);

      // Update local state
      await refetch();
      return data;
    } catch (err: any) {
      console.error(`Error updating template ${id}:`, err);
      setError(err);
      toast.error("Failed to update template: " + err.message);
      return null;
    } finally {
      setLoadingAction(false);
    }
  };

  const deleteTemplate = async (id: string): Promise<boolean> => {
    try {
      setLoadingAction(true);
      setError(null);
      
      await emailTemplateService.deleteTemplate(id);

      toast.success("Template deleted");

      // Update local state
      await refetch();
      return true;
    } catch (err: any) {
      setError(err);
      toast.error("Failed to delete template: " + err.message);
      return false;
    } finally {
      setLoadingAction(false);
    }
  };

  const sendTestEmail = async (
    templateId: string,
    to: string,
    testData?: Record<string, any>
  ): Promise<boolean> => {
    try {
      setLoadingAction(true);
      setError(null);
      
      await emailTemplateService.sendTestEmail(templateId, to, testData);

      toast.success(`Test email sent to ${to}`);

      return true;
    } catch (err: any) {
      setError(err);
      toast.error("Failed to send test email: " + err.message);
      return false;
    } finally {
      setLoadingAction(false);
    }
  };

  return {
    loading: isLoading,
    templates,
    fetchTemplates,
    fetchTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    sendTestEmail,
    loadingAction,
    error,
    pagination: {
      currentPage: page,
      totalPages,
      totalCount,
    }
  };
}
